import { useCallback, useEffect, useRef, useState } from 'react'
import { useRouter } from 'next/router'

import { derivePageContext } from '../../lib/analytics-core.mjs'
import { buildLinkInteractions } from '../../lib/analytics-interactions.mjs'
import { prepareOutboundHandoff } from '../../lib/analytics-handoff.mjs'
import {
  ANALYTICS_CONSENT_EVENT,
  ANALYTICS_INTERACTION_EVENT,
  hasAnalyticsConsent,
  trackCanonicalEvent,
} from '../../lib/analytics'

const ENGAGEMENT_THRESHOLDS = [10, 30, 60, 120, 300]
const SCROLL_THRESHOLDS = [25, 50, 75, 90]
const IMPRESSION_SELECTOR = [
  '[data-analytics-module-id]',
  '[data-analytics-listing-id]',
  '[data-analytics-cta-id]',
].join(',')

const compact = (value) =>
  Object.fromEntries(
    Object.entries(value).filter(([, item]) => item !== undefined && item !== null && item !== '')
  )

const elementDataset = (element) => {
  const item = element?.closest?.(
    '[data-analytics-listing-id], [data-analytics-business-id], [data-ga-item-name]'
  )
  return {
    ...(item?.dataset || {}),
    ...(element?.dataset || {}),
    analyticsBusinessName:
      element?.dataset?.analyticsBusinessName ||
      item?.dataset?.analyticsBusinessName ||
      element?.dataset?.gaItemName ||
      item?.dataset?.gaItemName,
    analyticsBusinessCategory:
      element?.dataset?.analyticsBusinessCategory ||
      item?.dataset?.analyticsBusinessCategory ||
      element?.dataset?.gaItemCategory ||
      item?.dataset?.gaItemCategory,
  }
}

const placementFor = (element) => {
  if (element?.dataset?.analyticsPlacementId) return element.dataset.analyticsPlacementId
  const region = element?.closest?.('header, nav, main, article, aside, footer')
  return region?.tagName?.toLowerCase() || 'page'
}

const impressionProperties = (element) => {
  const data = elementDataset(element)
  return compact({
    module_id: data.analyticsModuleId,
    module_type: data.analyticsModuleType,
    module_position: Number.isFinite(Number(data.analyticsPosition))
      ? Number(data.analyticsPosition)
      : undefined,
    section_heading: data.analyticsSectionHeading,
    listing_id: data.analyticsListingId,
    business_id: data.analyticsBusinessId,
    business_name: data.analyticsBusinessName,
    business_category: data.analyticsBusinessCategory,
    business_area: data.analyticsBusinessArea,
    business_section: data.analyticsBusinessSection,
    editors_pick: data.analyticsEditorsPick === 'true',
    listing_position: Number.isFinite(Number(data.analyticsPosition))
      ? Number(data.analyticsPosition)
      : undefined,
    cta_id: data.analyticsCtaId,
    cta_type: data.analyticsCtaType,
    cta_label: data.analyticsCtaLabel,
    placement_id: data.analyticsPlacementId || placementFor(element),
    threshold_version: '1.0.0',
  })
}

export default function AnalyticsTracker({ pageProps = {} }) {
  const router = useRouter()
  const [analyticsEnabled, setAnalyticsEnabled] = useState(false)
  const pageContextRef = useRef({})
  const lastPageKeyRef = useRef('')
  const seenImpressionsRef = useRef(new Set())
  const scrollThresholdsRef = useRef(new Set())
  const engagementThresholdsRef = useRef(new Set())
  const activeSecondsRef = useRef(0)
  const lastActivityRef = useRef(Date.now())
  const articleCompletedRef = useRef(false)

  const emit = useCallback((eventName, properties = {}) => {
    trackCanonicalEvent(eventName, {
      context: pageContextRef.current,
      properties,
    })
  }, [])

  const resetPageState = useCallback(() => {
    seenImpressionsRef.current = new Set()
    scrollThresholdsRef.current = new Set()
    engagementThresholdsRef.current = new Set()
    activeSecondsRef.current = 0
    lastActivityRef.current = Date.now()
    articleCompletedRef.current = false
  }, [])

  const recordPage = useCallback(
    (force = false) => {
      if (!router.isReady || !hasAnalyticsConsent()) return
      const context = derivePageContext({
        path: router.asPath,
        pageUrl: window.location.href,
        pageTitle: document.title,
        pageProps,
      })
      const pageKey = `${context.page_path}:${context.content_revision || context.content_id || ''}`
      if (!force && pageKey === lastPageKeyRef.current) return

      pageContextRef.current = context
      lastPageKeyRef.current = pageKey
      resetPageState()
      emit('page_viewed')
      if (context.page_type === 'listing_detail') {
        emit('listing_viewed', {
          listing_id: context.listing_id,
          business_id: context.business_id,
          business_name: context.business_name,
          business_category: context.business_category,
          business_area: context.business_area,
          business_section: context.business_section,
          editors_pick: context.editors_pick,
        })
      }
    },
    [emit, pageProps, resetPageState, router.asPath, router.isReady]
  )

  useEffect(() => {
    setAnalyticsEnabled(hasAnalyticsConsent())
    const frame = window.requestAnimationFrame(() => recordPage())
    const handleConsent = (event) => {
      const granted = event?.detail?.analytics === true
      setAnalyticsEnabled(granted)
      if (granted) {
        lastPageKeyRef.current = ''
        recordPage(true)
      }
    }
    window.addEventListener(ANALYTICS_CONSENT_EVENT, handleConsent)
    return () => {
      window.cancelAnimationFrame(frame)
      window.removeEventListener(ANALYTICS_CONSENT_EVENT, handleConsent)
    }
  }, [recordPage])

  useEffect(() => {
    const handleAnnouncedInteraction = (event) => {
      if (!hasAnalyticsConsent() || !event?.detail?.eventName) return
      emit(event.detail.eventName, event.detail.properties || {})
    }
    window.addEventListener(ANALYTICS_INTERACTION_EVENT, handleAnnouncedInteraction)
    return () =>
      window.removeEventListener(ANALYTICS_INTERACTION_EVENT, handleAnnouncedInteraction)
  }, [emit])

  useEffect(() => {
    const handleClick = (event) => {
      if (!hasAnalyticsConsent()) return
      const link = event.target.closest?.('a[href]')
      if (!link) return

      const handoff = prepareOutboundHandoff({
        href: link.href,
        baseUrl: window.location.href,
        enabled: true,
      })
      if (handoff.handoffId) link.href = handoff.url

      const data = {
        ...elementDataset(link),
        ...elementDataset(event.target),
      }
      const interactions = buildLinkInteractions({
        href: handoff.url,
        text: link.textContent || link.getAttribute('aria-label') || '',
        currentHost: window.location.hostname,
        dataset: {
          ...data,
          analyticsPlacementId: data.analyticsPlacementId || placementFor(link),
        },
        interactionType: 'click',
        handoffId: handoff.handoffId,
      })
      for (const interaction of interactions) {
        emit(interaction.eventName, interaction.properties)
      }
    }

    document.addEventListener('click', handleClick, true)
    return () => document.removeEventListener('click', handleClick, true)
  }, [emit])

  useEffect(() => {
    if (!analyticsEnabled || typeof IntersectionObserver === 'undefined') return undefined
    const timers = new Map()

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const element = entry.target
          const data = elementDataset(element)
          const kind = data.analyticsCtaId
            ? 'cta'
            : data.analyticsListingId
              ? 'listing'
              : 'content'
          const id =
            data.analyticsCtaId ||
            data.analyticsListingId ||
            data.analyticsModuleId
          const key = `${kind}:${id || data.analyticsPosition || 'unknown'}`

          if (!entry.isIntersecting || entry.intersectionRatio < 0.5) {
            if (timers.has(element)) window.clearTimeout(timers.get(element))
            timers.delete(element)
            continue
          }
          if (seenImpressionsRef.current.has(key) || timers.has(element)) continue

          const delay = kind === 'cta' ? 500 : kind === 'listing' ? 750 : 1000
          timers.set(
            element,
            window.setTimeout(() => {
              timers.delete(element)
              if (!element.isConnected || seenImpressionsRef.current.has(key)) return
              seenImpressionsRef.current.add(key)
              emit(
                kind === 'listing'
                  ? 'listing_impression'
                  : kind === 'cta'
                    ? 'cta_impression'
                    : 'content_impression',
                impressionProperties(element)
              )
            }, delay)
          )
        }
      },
      { threshold: [0.5] }
    )

    const observe = (root = document) => {
      if (root.matches?.(IMPRESSION_SELECTOR)) observer.observe(root)
      root.querySelectorAll?.(IMPRESSION_SELECTOR).forEach((element) => observer.observe(element))
    }
    observe()

    const mutations = new MutationObserver((entries) => {
      for (const entry of entries) entry.addedNodes.forEach((node) => observe(node))
    })
    mutations.observe(document.body, { childList: true, subtree: true })

    return () => {
      mutations.disconnect()
      observer.disconnect()
      timers.forEach((timer) => window.clearTimeout(timer))
    }
  }, [analyticsEnabled, emit, router.asPath])

  useEffect(() => {
    const noteActivity = () => {
      lastActivityRef.current = Date.now()
    }

    const maybeCompleteArticle = () => {
      if (
        !articleCompletedRef.current &&
        pageContextRef.current.page_type === 'article_detail' &&
        scrollThresholdsRef.current.has(90) &&
        activeSecondsRef.current >= 30
      ) {
        articleCompletedRef.current = true
        emit('article_completed', {
          engagement_milliseconds: activeSecondsRef.current * 1000,
          threshold_version: '1.0.0',
        })
      }
    }

    const handleScroll = () => {
      noteActivity()
      if (!hasAnalyticsConsent()) return
      const scrollable = document.documentElement.scrollHeight - window.innerHeight
      if (scrollable <= 0) return
      const percent = Math.min(100, Math.round((window.scrollY / scrollable) * 100))
      for (const threshold of SCROLL_THRESHOLDS) {
        if (percent >= threshold && !scrollThresholdsRef.current.has(threshold)) {
          scrollThresholdsRef.current.add(threshold)
          emit('scroll_depth_reached', {
            scroll_percent: threshold,
            threshold_version: '1.0.0',
          })
        }
      }
      maybeCompleteArticle()
    }

    const interval = window.setInterval(() => {
      if (
        !hasAnalyticsConsent() ||
        document.visibilityState !== 'visible' ||
        Date.now() - lastActivityRef.current > 30_000
      ) {
        return
      }
      activeSecondsRef.current += 1
      for (const threshold of ENGAGEMENT_THRESHOLDS) {
        if (
          activeSecondsRef.current >= threshold &&
          !engagementThresholdsRef.current.has(threshold)
        ) {
          engagementThresholdsRef.current.add(threshold)
          emit('content_engaged', {
            engagement_threshold_seconds: threshold,
            engagement_milliseconds: activeSecondsRef.current * 1000,
            threshold_version: '1.0.0',
          })
        }
      }
      maybeCompleteArticle()
    }, 1000)

    window.addEventListener('scroll', handleScroll, { passive: true })
    window.addEventListener('pointerdown', noteActivity, { passive: true })
    window.addEventListener('keydown', noteActivity)
    return () => {
      window.clearInterval(interval)
      window.removeEventListener('scroll', handleScroll)
      window.removeEventListener('pointerdown', noteActivity)
      window.removeEventListener('keydown', noteActivity)
    }
  }, [emit])

  useEffect(() => {
    const startedForms = new WeakSet()
    const formId = (form) =>
      form?.dataset?.analyticsFormId || form?.id || form?.getAttribute('name') || 'form'

    const handleFormStart = (event) => {
      if (!hasAnalyticsConsent()) return
      const form = event.target.closest?.('form')
      if (!form || startedForms.has(form)) return
      startedForms.add(form)
      emit('form_started', { form_id: formId(form) })
    }
    const handleSubmit = (event) => {
      if (!hasAnalyticsConsent()) return
      emit('form_submitted', { form_id: formId(event.target) })
    }
    const handleChange = (event) => {
      if (
        !hasAnalyticsConsent() ||
        (event.target.id !== 'listing-category' && !event.target.dataset.analyticsFilterType)
      ) return
      emit('filter_applied', {
        filter_type: event.target.dataset.analyticsFilterType || 'business_category',
        filter_value: event.target.value,
      })
    }
    const handleError = () => {
      if (hasAnalyticsConsent()) emit('error_experienced', { error_code: 'javascript_error' })
    }
    const handleRejection = () => {
      if (hasAnalyticsConsent()) emit('error_experienced', { error_code: 'unhandled_rejection' })
    }

    document.addEventListener('focusin', handleFormStart, true)
    document.addEventListener('submit', handleSubmit, true)
    document.addEventListener('change', handleChange, true)
    window.addEventListener('error', handleError)
    window.addEventListener('unhandledrejection', handleRejection)
    return () => {
      document.removeEventListener('focusin', handleFormStart, true)
      document.removeEventListener('submit', handleSubmit, true)
      document.removeEventListener('change', handleChange, true)
      window.removeEventListener('error', handleError)
      window.removeEventListener('unhandledrejection', handleRejection)
    }
  }, [emit])

  return null
}
